import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";

import { fetchStoryboardReportSummary, fetchReportsDataForDownload, invalidateDownloadData } from 'actions/storyboardReports';

import dateformat from "dateformat";
import _ from 'lodash';
import { Button } from "react-bootstrap";
import cx from 'classnames';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import StoryboardContext from 'components/Storyboard/context';
import { convertDatesToTimezone } from 'services/report.service';

import { VerticalTile, HorizontalRightTile } from '.';
import { CSVLink } from "react-csv";
import { createMixpanelEvent } from "actions/mixpanel";
import { MIXPANEL_REPORTS_DOWNLOADED } from "constants/mixpanelEvents";
import OverAllFilterHeader from "./OverAllFilterHeader";
import { useReportType } from "../ReportsProvider";
import { REPORT_SUMMARY_LEFT } from "../constants/reports";
import { formatDurationWithLabel, formatNumber } from '../reporthelpers';

import styles from '../report.module.scss';
import { INTERACTIVE_CONTENT_TYPES } from "constants/storyboardType";

export function ReportSummary(props) {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();
  const summary = useSelector(state => _.get(state, 'storyboards.storyboard.reports.summary.item', {}));
  const page = useSelector(state => _.get(state, 'storyboards.storyboard.item.page', {}));
  const published = useSelector(state => _.get(state, 'storyboards.storyboard.item.page.published', {}));
  const { reportType } = useReportType()
  const { storyboardType }  = props

  useEffect(() => {
    var request = {
      storyboard: storyboardId,
      reportType,
    }
    if (reportType) {
      dispatch(fetchStoryboardReportSummary(request));
    }
  }, [reportType]);
  
  const averageTimeSpent = formatDurationWithLabel(summary.averageTimeSpent)
  const totalTimeSpent = formatDurationWithLabel(summary.totalTimeSpent)
  
  const reportInsights = useMemo(() => {
    const insights = REPORT_SUMMARY_LEFT[reportType].map(item => {
      return ({
        path: item.path,
        title: item.title,
        description: item.description,
        value: summary[item.accessorKey] ?? null
      })
    })
    if (INTERACTIVE_CONTENT_TYPES.includes(storyboardType)) {
      insights.push({
        path: '/images/reports/downloadV2.svg',
        title: 'Download',
        description: 'Total Number',
        value: summary['downloads'] ?? null
      })
    }
    return insights
  }, [summary, reportType]);

  return (
    <div className={styles.summary}>
      <div className={styles.summaryHeading}>
        <div className={styles.left}>
          <h1 className={styles.reportsHeading}>Reports</h1>
          <span className={styles.reportsDescription}>A telescopic view of how your content is performing</span>
        </div>

        <div className={styles.right}>
          <ExportCSV date={props.date} />
          <div className={styles.creator}>
            <span className={styles.heading}>Created by: </span>
            <span className={styles.value}>{_.get(page, 'createdBy.firstName') || ''} {_.get(page, 'createdBy.lastName') || ''}</span>
          </div>

          <div className={styles.updatedInfo}>
            {published && <>
              <div className={styles.published}>
                <span className={styles.heading}>Published: </span>
                <span className={styles.value}>{dateformat(page.lastPublished, 'dd mmm yyyy')}</span>
              </div>
              |
            </>
            }

            <div className={cx({ [styles.modified]: published })}>
              <span className={styles.heading}>Modified: </span>
              <span className={styles.value}>{dateformat(page.modifiedDate, 'dd mmm yyyy')}</span>
            </div>
          </div>
        </div>
      </div>
      <OverAllFilterHeader />
      <div className={styles.summaryBody}>
        <div className={styles.tileContainer}>
          <div className={styles.reportInsights}>
            {reportInsights.map((tile, index) => (
              <VerticalTile
                key={index}
                title={tile.title}
                path={tile.path}
                description={tile.description}
                value={formatNumber(tile.value)}
              />
            ))}
            <div className={styles.rightInsight}>
              <HorizontalRightTile
                totalTimeLabel={totalTimeSpent.label}
                avgTimeLabel={averageTimeSpent.label}
                avgTime={averageTimeSpent.value}
                totalTime={totalTimeSpent.value}
                splitView={true}
              />
              {/* Will be enabled once the backend data for summary chart is ready */}
              {/* {reportType !== REPORT_TYPES.PERSONALIZATION.value && (
                <div className={styles.chart}>
                  <div className={styles.conversionWrapper}>
                    <div className={styles.upper}>
                      <span className={styles.title}>Completion Rate</span>
                      <span className={styles.desc}></span>
                    </div>
                    <span className={styles.conversionValue}>
                      {formatNumber(summary.completion)}%
                    </span>
                  </div>
                  <div className={styles.graphWrapper}></div>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExportCSV(props) {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();
  let downloads = useSelector(state => _.get(state, 'storyboards.storyboard.reports.download.items'));
  const completed = useSelector(state => _.get(state, 'storyboards.storyboard.reports.download.completed'));
  const progress = useSelector(state => _.get(state, 'storyboards.storyboard.reports.download.progress'));
  const [csvData, setCsvData] = useState([]);
  const csvLink = useRef();
  const { reportType } = useReportType()

  const headers = [
    { label: "Visitor", key: "visitor" },
    { label: "Email", key: "email" },
    { label: "Last Visited Date", key: "lastVisitedDate" },
    { label: "Source", key: "source" },
    { label: "Medium", key: "medium" },
    { label: "Page Landed on", key: "landedPage" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "Country", key: "country" },
    { label: "Device", key: "device" },
    { label: "Browser", key: "browser" },
    { label: "Time Spent", key: "time_spent" },
    { label: "Completion", key: "completion" }
  ];

  const renderVisitorName = (data) => {
    var name = `${_.get(data, 'firstName') || ''} ${_.get(data, 'lastName') || ''} `;
    return name;
  }

  const renderLocation = (data, locationType) => {
    var city = _.get(data, 'country.city', '') || '';
    var country = _.get(data, 'country.country_name', '') || '';
    var region = _.get(data, 'country.region_name', '') || '';
    
    if (!city && !country && !region) {
      return '';
    }
    
    const locationMap = {
      city,
      country,
      region
    };

    return locationMap[locationType];
  }

  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  useEffect(() => {
    if (completed) {
      var columns = [];
      downloads && downloads.map((data, index) => {
        columns[index] = {
          visitor: renderVisitorName(data.visitor),
          email: data.visitor.email,
          lastVisitedDate: dateformat(data.lastVisited, 'dd mmm yyyy'),
          source: data.campaign && data.campaign.source,
          medium: data.campaign && data.campaign.medium,
          landedPage: data.url,
          city: renderLocation(data, 'city'),
          state: renderLocation(data, 'region'),
          country: renderLocation(data, 'country'),
          device: data.device,
          browser: data.browser,
          time_spent: formatDurationWithLabel(data.timeSpent).value,
          completion: `${Math.round(data.completion)} %`
        }
      });

    setCsvData(columns);
    }
  }, [completed]);

  useEffect(() => {
    if (!completed && csvData && csvData.length == 0) {
      return;
    }

    csvLink.current.link.click();

    return () => {
      dispatch(invalidateDownloadData());
    }
  }, [csvData]);

  const fetchReportsData = () => {
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboard.id, storyboard.name, storyboard.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      report_type: 'Master'  
    }
 
      dispatch(createMixpanelEvent(MIXPANEL_REPORTS_DOWNLOADED, mixpanelPayload));  
      dispatch(invalidateDownloadData());


    var request = {
      storyboard: storyboardId,
      reportType,
      ...convertDatesToTimezone(props.date),
    }

    dispatch(fetchReportsDataForDownload(request, 0, 50));
  }

  return (
    <div>
      <Button variant="primary" disabled={progress} onClick={() => fetchReportsData()} className={styles.download}>
        <span className={styles.name}>{progress ? "Downloading..." : "Download"}</span>
        <i className={cx("cleverstory-download", styles.downloadIcon)} />
      </Button>
      <CSVLink
        ref={csvLink}
        headers={headers}
        data={csvData}
        filename='Reports.csv'
        className='hidden'
      />
    </div>
  )
}